import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";
import { PageHeader, StatCard, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, numberID, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Warehouse, PackagePlus, PackageMinus, Boxes, Lock, Package, AlertTriangle, Eye, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";

const pageList = (cur, tp) => {
  if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
  const out = [1];
  const s = Math.max(2, cur - 1), e = Math.min(tp - 1, cur + 1);
  if (s > 2) out.push("...");
  for (let i = s; i <= e; i++) out.push(i);
  if (e < tp - 1) out.push("...");
  out.push(tp);
  return out;
};

export default function InventoryPusat() {
  const { can } = useAuth();
  const { masters, loading: mLoading } = useMasters();
  const [pusat, setPusat] = useState(null);
  const [val, setVal] = useState(null);
  const [low, setLow] = useState(null);
  const [move, setMove] = useState(null); // {type: 'IN'|'OUT'}

  useEffect(() => {
    api.get("/inventory/warehouses").then((r) => setPusat(r.data.find((w) => w.type === "PUSAT")));
    reloadSummary();
  }, []);
  const reloadSummary = () => {
    api.get("/inventory/valuation").then((r) => setVal(r.data)).catch(() => {});
    api.get("/inventory/low-stock").then((r) => setLow(r.data)).catch(() => {});
  };

  return (
    <div>
      <PageHeader title="Inventory Pusat" subtitle="Barang jadi = stok bersama global untuk semua toko. Tidak dibagi per toko." testId="pusat-header">
        {can("inventory.transact") && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMove({ type: "IN" })} data-testid="barang-masuk-btn"><PackagePlus className="mr-1.5 h-4 w-4 text-emerald-600" /> Barang Masuk</Button>
            <Button variant="outline" onClick={() => setMove({ type: "OUT" })} data-testid="barang-keluar-btn"><PackageMinus className="mr-1.5 h-4 w-4 text-rose-600" /> Barang Keluar</Button>
          </div>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Stok Fisik" value={numberID(val?.total_qty)} icon={Boxes} accent="slate" testId="pusat-total-qty" />
        <StatCard label="Nilai Persediaan" value={rupiah(val?.total_value)} icon={Warehouse} accent="indigo" hint="Berdasarkan HPP" testId="pusat-valuation" />
        <StatCard label="Low Stock" value={numberID(low?.low_stock?.length)} icon={AlertTriangle} accent="amber" testId="pusat-low" />
        <StatCard label="Out of Stock" value={numberID(low?.out_of_stock?.length)} icon={AlertTriangle} accent="rose" testId="pusat-out" />
      </div>

      <div className="mt-6">
        <Tabs defaultValue="matrix">
          <TabsList>
            <TabsTrigger value="matrix" data-testid="tab-matrix">Stock Matrix</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">Stok Barang Jadi</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Riwayat Stok</TabsTrigger>
            <TabsTrigger value="lowstock" data-testid="tab-lowstock">Minimum Stock</TabsTrigger>
          </TabsList>
          <TabsContent value="matrix" className="mt-4"><MatrixView masters={masters} loading={mLoading} /></TabsContent>
          <TabsContent value="list" className="mt-4"><BalanceList /></TabsContent>
          <TabsContent value="history" className="mt-4"><HistoryView /></TabsContent>
          <TabsContent value="lowstock" className="mt-4"><LowStockView low={low} /></TabsContent>
        </Tabs>
      </div>

      {move && pusat && <MoveDialog type={move.type} warehouseId={pusat.id} onClose={() => setMove(null)} onSaved={() => { setMove(null); reloadSummary(); }} />}
    </div>
  );
}

function MatrixView({ masters, loading }) {
  const [f, setF] = useState({ fabric_type_id: "", age_category_id: "", sleeve_type_id: "", color_id: "" });
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (masters.fabric_types.length && !f.fabric_type_id) {
      setF({
        fabric_type_id: masters.fabric_types[0].id,
        age_category_id: masters.age_categories.find((a) => a.code === "ADULT")?.id || masters.age_categories[0]?.id,
        sleeve_type_id: masters.sleeve_types[0].id,
        color_id: masters.colors[0].id,
      });
    }
  }, [masters]);

  useEffect(() => {
    if (!f.fabric_type_id || !f.age_category_id || !f.sleeve_type_id || !f.color_id) return;
    setBusy(true);
    api.get("/inventory/pusat/matrix", { params: f }).then((r) => setRows(r.data.sizes)).catch((e) => toast.error(apiError(e))).finally(() => setBusy(false));
  }, [f]);

  if (loading) return <Loading />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Sel label="Bahan" value={f.fabric_type_id} onChange={(v) => setF({ ...f, fabric_type_id: v })} options={masters.fabric_types} testId="m-fabric" />
        <Sel label="Umur" value={f.age_category_id} onChange={(v) => setF({ ...f, age_category_id: v })} options={masters.age_categories} testId="m-age" />
        <Sel label="Lengan" value={f.sleeve_type_id} onChange={(v) => setF({ ...f, sleeve_type_id: v })} options={masters.sleeve_types} testId="m-sleeve" />
        <Sel label="Warna" value={f.color_id} onChange={(v) => setF({ ...f, color_id: v })} options={masters.colors} testId="m-color" />
      </div>
      {busy ? <Loading /> : !rows ? null : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ukuran</TableHead><TableHead>SKU</TableHead>
                <TableHead className="text-right">On Hand</TableHead>
                <TableHead className="text-right">Reserved</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Min</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const status = !r.variant_id ? null : r.available <= 0 ? "OUT" : (r.minimum_stock > 0 && r.available <= r.minimum_stock ? "LOW" : "OK");
                return (
                  <TableRow key={r.size_id} className={cn(r.available <= 5 && r.variant_id && "bg-rose-50/40")} data-testid={`matrix-row-${r.size_code}`}>
                    <TableCell className="font-semibold">{r.size_name}</TableCell>
                    <TableCell className="font-mono text-xs text-indigo-700">{r.sku || <span className="text-slate-300">belum ada varian</span>}</TableCell>
                    <TableCell className="text-right">{numberID(r.on_hand)}</TableCell>
                    <TableCell className="text-right text-amber-600">{numberID(r.reserved)}</TableCell>
                    <TableCell className="text-right font-bold">{numberID(r.available)}</TableCell>
                    <TableCell className="text-right text-slate-400">{numberID(r.minimum_stock)}</TableCell>
                    <TableCell>{status && <StatusBadge status={status} label={status === "OUT" ? "Habis" : status === "LOW" ? "Menipis" : "Normal"} />}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function BalanceList() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/inventory/balances", { params: { ref_type: "variant" } }).then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loading />;
  if (rows.length === 0) return <EmptyState title="Belum ada stok barang jadi" subtitle="Lakukan Barang Masuk untuk menambahkan stok." icon={Package} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">On Hand</TableHead><TableHead className="text-right">Reserved</TableHead><TableHead className="text-right">Available</TableHead><TableHead className="text-right">Non-Jual</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((b) => (
            <TableRow key={b.id} data-testid={`balance-row-${b.sku}`}>
              <TableCell className="font-mono text-xs font-semibold text-indigo-700">{b.sku}</TableCell>
              <TableCell className="text-sm text-slate-500">{b.variant?.fabric_name} · {b.variant?.age_name} · {b.variant?.sleeve_name} · {b.variant?.color_name} · {b.variant?.size_name}</TableCell>
              <TableCell className="text-right">{numberID(b.on_hand)}</TableCell>
              <TableCell className="text-right text-amber-600">{numberID(b.reserved)}</TableCell>
              <TableCell className="text-right font-bold">{numberID(b.available)}</TableCell>
              <TableCell className="text-right text-rose-600" data-testid={`non-sellable-${b.sku}`}>{numberID(b.non_sellable || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function HistoryView() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [jump, setJump] = useState("");
  const [detailId, setDetailId] = useState(null);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    api.get("/inventory/transactions/count", { params: { ref_type: "variant" } })
      .then((r) => setTotal(r.data.total)).catch(() => {});
  }, []);
  useEffect(() => {
    setRows(null);
    api.get("/inventory/transactions", { params: { ref_type: "variant", skip: (page - 1) * limit, limit } })
      .then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, [page, limit]);
  useEffect(() => { setPage(1); }, [limit]);

  const goJump = () => {
    const n = Number(jump);
    if (n >= 1 && n <= totalPages) { setPage(n); setJump(""); }
    else toast.error(`Halaman harus antara 1 dan ${totalPages}`);
  };

  if (rows === null) return <Loading />;
  if (total === 0) return <EmptyState title="Belum ada riwayat" icon={Boxes} />;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="pusat-history">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>SKU</TableHead><TableHead>Tipe</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Sebelum</TableHead><TableHead className="text-right">Sesudah</TableHead><TableHead>Referensi</TableHead><TableHead>Oleh</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id} data-testid={`history-row-${t.id}`}>
                <TableCell className="text-xs text-slate-500">{formatDateTime(t.created_at)}</TableCell>
                <TableCell className="font-mono text-xs">{t.label}</TableCell>
                <TableCell><StatusBadge status={t.qty >= 0 ? "OK" : "OUT"} label={t.type_label || t.type} /></TableCell>
                <TableCell className={cn("text-right font-semibold", t.qty >= 0 ? "text-emerald-600" : "text-rose-600")}>{t.qty >= 0 ? "+" : ""}{numberID(t.qty)}</TableCell>
                <TableCell className="text-right text-slate-400">{t.qty_before == null ? <span className="text-slate-300">—</span> : numberID(t.qty_before)}</TableCell>
                <TableCell className="text-right">{t.qty_after == null ? <span className="text-slate-300">—</span> : numberID(t.qty_after)}</TableCell>
                <TableCell className="text-xs text-slate-500">{t.reference || "-"}</TableCell>
                <TableCell className="text-xs text-slate-500">{t.created_by_name || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => setDetailId(t.id)} data-testid={`history-detail-btn-${t.id}`}><Eye className="mr-1 h-3.5 w-3.5" /> Detail</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
        <div className="flex items-center gap-3">
          <span data-testid="history-range">{total === 0 ? "0 transaksi" : `Menampilkan ${numberID((page - 1) * limit + 1)}–${numberID(Math.min(page * limit, total))} dari ${numberID(total)} transaksi`}</span>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="h-8 w-[130px]" data-testid="history-page-size"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="20">20 / halaman</SelectItem><SelectItem value="50">50 / halaman</SelectItem><SelectItem value="100">100 / halaman</SelectItem></SelectContent>
          </Select>
          <span className="text-xs text-slate-400">Hal. {page} / {totalPages}</span>
        </div>
        <div className="flex items-center gap-1" data-testid="history-pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} data-testid="page-first"><ChevronsLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
          {pageList(page, totalPages).map((p, i) => p === "..."
            ? <span key={`e${i}`} className="px-2 text-slate-400">…</span>
            : <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className={p === page ? "bg-indigo-600 hover:bg-indigo-500" : ""} onClick={() => setPage(p)} data-testid={`page-${p}`}>{p}</Button>)}
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="page-last"><ChevronsRight className="h-4 w-4" /></Button>
          <Input className="ml-2 h-8 w-16" placeholder="Hal." value={jump} onChange={(e) => setJump(e.target.value.replace(/[^0-9]/g, ""))} onKeyDown={(e) => e.key === "Enter" && goJump()} data-testid="history-page-jump-input" />
          <Button variant="outline" size="sm" onClick={goJump} data-testid="history-page-jump-btn">Lompat</Button>
        </div>
      </div>
      {detailId && <TxnDetailDialog txnId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function TxnDetailDialog({ txnId, onClose }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/inventory/transactions/${txnId}`).then((r) => setD(r.data)).catch((e) => setErr(apiError(e)));
  }, [txnId]);
  const NR = <span className="text-slate-400 italic">Tidak tercatat</span>;
  const show = (v) => (v == null ? NR : v);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="txn-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">Detail Transaksi</DialogTitle></DialogHeader>
        {err ? <p className="text-sm text-rose-600">{err}</p> : !d ? <Loading /> : (
          <div className="space-y-3 text-sm">
            <Field label="ID Transaksi" value={<span className="font-mono text-xs">{d.id}</span>} testId="txn-d-id" />
            <Field label="Waktu (WIB)" value={show(d.created_at ? formatDateTime(d.created_at) : null)} testId="txn-d-time" />
            <Field label="Tipe" value={<StatusBadge status={d.qty >= 0 ? "OK" : "OUT"} label={d.type_label || d.type} />} testId="txn-d-type" />
            <Field label="SKU / Item" value={<span className="font-mono text-xs">{show(d.label)}</span>} testId="txn-d-sku" />
            <div className="grid grid-cols-3 gap-2">
              <Field label="Perubahan Qty" value={<span className={cn("font-semibold", d.qty >= 0 ? "text-emerald-600" : "text-rose-600")}>{d.qty >= 0 ? "+" : ""}{numberID(d.qty)} {d.unit || ""}</span>} testId="txn-d-qty" />
              <Field label="On Hand Sebelum" value={d.qty_before == null ? NR : numberID(d.qty_before)} testId="txn-d-before" />
              <Field label="On Hand Sesudah" value={d.qty_after == null ? NR : numberID(d.qty_after)} testId="txn-d-after" />
            </div>
            <Field label="Reserved (historis)" value={d.reserved == null ? NR : numberID(d.reserved)} testId="txn-d-reserved" />
            <Field label="Gudang" value={show(d.warehouse_name)} testId="txn-d-warehouse" />
            <Field label="Dokumen Referensi" value={show(d.reference)} testId="txn-d-ref" />
            <Field label="Oleh" value={show(d.created_by_name)} testId="txn-d-user" />
            <Field label="Catatan" value={show(d.notes)} testId="txn-d-notes" />
            {d.opname_no && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
                <p className="text-xs text-slate-500">Terkait Stock Opname: <span className="font-mono font-semibold text-indigo-700">{d.opname_no}</span></p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => { onClose(); navigate(`/inventory/opname?open=${d.opname_id}`); }} data-testid="txn-d-lihat-opname">
                  <ClipboardList className="mr-1.5 h-4 w-4" /> Lihat Opname
                </Button>
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="txn-detail-close">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-4" data-testid={testId}>
    <span className="text-xs text-slate-400">{label}</span>
    <span className="text-right text-slate-700">{value}</span>
  </div>
);

function LowStockView({ low }) {
  if (!low) return <Loading />;
  const all = [...(low.out_of_stock || []).map((r) => ({ ...r, _s: "OUT" })), ...(low.low_stock || []).map((r) => ({ ...r, _s: "LOW" }))];
  if (all.length === 0) return <EmptyState title="Semua stok aman" subtitle="Tidak ada SKU di bawah minimum stok." icon={Package} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead className="text-right">Available</TableHead><TableHead className="text-right">Minimum</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {all.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs font-semibold text-indigo-700">{r.sku}</TableCell>
              <TableCell className="text-right font-bold">{numberID(r.available)}</TableCell>
              <TableCell className="text-right text-slate-400">{numberID(r.minimum_stock)}</TableCell>
              <TableCell><StatusBadge status={r._s} label={r._s === "OUT" ? "Habis" : "Menipis"} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MoveDialog({ type, warehouseId, onClose, onSaved }) {
  const [variants, setVariants] = useState([]);
  const [form, setForm] = useState({ ref_id: "", qty: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/products/variants", { params: { status: "active", limit: 500 } }).then((r) => setVariants(r.data.items)); }, []);
  const save = async () => {
    if (!form.ref_id || Number(form.qty) <= 0) return toast.error("Pilih SKU & qty > 0");
    setSaving(true);
    try {
      const url = type === "IN" ? "/inventory/barang-masuk" : "/inventory/barang-keluar";
      await api.post(url, { warehouse_id: warehouseId, ref_type: "variant", ref_id: form.ref_id, qty: Number(form.qty), notes: form.notes });
      toast.success(type === "IN" ? "Barang masuk dicatat" : "Barang keluar dicatat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{type === "IN" ? "Barang Masuk" : "Barang Keluar"} — Inventory Pusat</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs text-slate-500">SKU / Varian</Label>
            <Select value={form.ref_id} onValueChange={(v) => setForm({ ...form, ref_id: v })}>
              <SelectTrigger className="mt-1" data-testid="move-variant"><SelectValue placeholder="Pilih SKU..." /></SelectTrigger>
              <SelectContent>{variants.map((v) => <SelectItem key={v.id} value={v.id}><span className="font-mono text-xs">{v.sku}</span> · tersedia {v.available}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-slate-500">Jumlah</Label><Input type="number" className="mt-1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="move-qty" /></div>
          <div><Label className="text-xs text-slate-500">Catatan</Label><Input className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="move-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-move-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Sel = ({ label, value, onChange, options, testId }) => (
  <div><Label className="text-xs text-slate-500">{label}</Label>
    <Select value={value} onValueChange={onChange}><SelectTrigger className="mt-1" data-testid={testId}><SelectValue placeholder="Pilih..." /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
  </div>
);
